/**
 * Walmart SSO Integration Service
 * Handles OAuth 2.0 flow with Walmart through the walmart-service adapter
 */

import axios from "axios";
import { walmartAdapterUrl, walmartAuthUrl, walmartClientId } from "../constant";

// Skip ngrok browser warning for all adapter API calls
axios.defaults.headers.common['ngrok-skip-browser-warning'] = 'true';

// Re-export for components that need the URLs
export const WALMART_ADAPTER_URL = walmartAdapterUrl;
export const WALMART_AUTH_URL = walmartAuthUrl;
export const WALMART_CLIENT_ID = walmartClientId;

// Walmart OAuth Configuration
const WALMART_REDIRECT_URI = `${walmartAdapterUrl}/auth/walmart/callback`;
const WALMART_CLIENT_TYPE = 'seller'; // For US marketplace

/**
 * Generate a random nonce for OAuth security
 * @returns {string} Random alphanumeric string
 */
const generateNonce = () => {
    return Math.random().toString(36).substring(2, 12).toUpperCase();
};

/**
 * Generate a random state for OAuth state management
 * Encodes the seller context so the adapter can link accounts
 * @param {object} sellerContext - The seller context with companySellerId, sellerId, companyId
 * @returns {string} Base64 encoded state containing seller info and random token
 */
const generateState = (sellerContext, flow = null) => {
    const randomToken = Math.random().toString(36).substring(2, 12).toUpperCase();
    // Encode seller context in state so adapter can link 3PLVision seller to Walmart account
    const stateData = {
        companySellerId: sellerContext.companySellerId,
        sellerId: sellerContext.sellerId,
        companyId: sellerContext.companyId,
        token: randomToken,
        timestamp: Date.now(),
        // UI origin so adapter knows where to redirect back (no hardcoded URLs needed)
        uiOrigin: window.location.origin,
    };
    if (flow) {
        stateData.flow = flow;
    }
    return btoa(JSON.stringify(stateData));
};

/**
 * Check Walmart connection status for a seller
 * @param {string|number} sellerId - The seller ID to check status for
 * @returns {Promise<{connected: boolean, connectedAt?: string}>}
 */
export const checkWalmartConnectionStatus = async (sellerId) => {
    try {
        const response = await axios.get(`${WALMART_ADAPTER_URL}/auth/walmart/status/${sellerId}`);
        return {
            connected: response?.data?.data?.connected || false,
            connectedAt: response?.data?.data?.connectedAt || null,
        };
    } catch (error) {
        console.error('Error checking Walmart connection status:', error);
        return { connected: false, connectedAt: null };
    }
};

/**
 * Get the current seller ID from storage
 * Checks currentCompany (selected context), userDetails, and user objects
 * @returns {{companySellerId: string|null, sellerId: string|null, companyId: string|null}}
 */
const getCurrentSellerContext = () => {
    // Priority 1: Check currentCompany (user's selected company/seller context)
    const currentCompany = JSON.parse(localStorage.getItem('currentCompany') || '{}');
    if (currentCompany?.companySellerId) {
        return {
            companySellerId: currentCompany.companySellerId,
            sellerId: currentCompany.sellerId,
            companyId: currentCompany.companyId,
            companySellerName: currentCompany.companySellerName
        };
    }
    
    // Priority 2: Check userDetails (login response)
    const userDetails = JSON.parse(localStorage.getItem('userDetails') || '{}');
    if (userDetails?.Value?.companySellerId) {
        return {
            companySellerId: userDetails.Value.companySellerId,
            sellerId: userDetails.Value.sellerId,
            companyId: userDetails.Value.companyId,
            companySellerName: null
        };
    }
    
    // Priority 3: Check user object
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (user?.Value?.companySellerId) {
        return {
            companySellerId: user.Value.companySellerId,
            sellerId: user.Value.sellerId,
            companyId: user.Value.companyId,
            companySellerName: null
        };
    }
    
    return { companySellerId: null, sellerId: null, companyId: null, companySellerName: null };
};

/**
 * Check if user has a valid seller context
 * @returns {boolean}
 */
export const hasSellerContext = () => {
    const context = getCurrentSellerContext();
    return !!context.companySellerId;
};

/**
 * Initiate Walmart OAuth flow by redirecting directly to Walmart login
 * @param {object} sellerContextOverride - Optional seller context to use instead of auto-detecting
 *        Should have: { companySellerId, sellerId, companyId }
 */
export const initiateWalmartOAuth = (sellerContextOverride = null) => {
    let sellerContext;
    
    if (sellerContextOverride && sellerContextOverride.companySellerId) {
        // Use provided context
        sellerContext = {
            companySellerId: sellerContextOverride.companySellerId,
            sellerId: sellerContextOverride.sellerId,
            companyId: sellerContextOverride.companyId
        };
    } else {
        // Auto-detect from storage
        sellerContext = getCurrentSellerContext();
    }
    
    if (!sellerContext.companySellerId) {
        console.error('Cannot initiate Walmart OAuth: No seller context available');
        throw new Error('Please select a Company/Seller before connecting to Walmart');
    }
    
    const nonce = generateNonce();
    const state = generateState(sellerContext);
    
    // Store state in sessionStorage for validation on callback
    sessionStorage.setItem('walmart_oauth_state', state);
    sessionStorage.setItem('walmart_oauth_company_seller_id', sellerContext.companySellerId);
    
    // Construct URL matching Walmart docs example format
    const authUrl = `${WALMART_AUTH_URL}?responseType=code&clientId=${WALMART_CLIENT_ID}&redirectUri=${WALMART_REDIRECT_URI}&nonce=${nonce}&state=${state}&clientType=${WALMART_CLIENT_TYPE}`;
    
    console.log('Redirecting to Walmart OAuth for companySellerId:', sellerContext.companySellerId);
    window.location.href = authUrl;
};

/**
 * Initiate Walmart OAuth flow for Walmart-initiated connection (Flow 2)
 * Walmart provides walmartCallbackUri (their authorization server URL) when seller
 * clicks "Connect" from Walmart Seller Center.
 *
 * @param {string} walmartCallbackUri - Walmart authorization server URL (used as base URL to redirect to)
 * @param {string} clientType - The client type (seller, seller-ca, seller-mx)
 */
export const initiateWalmartOAuthWithParams = (walmartCallbackUri, clientType) => {
    const sellerContext = getCurrentSellerContext();

    if (!sellerContext.companySellerId) {
        console.error('Cannot initiate Walmart OAuth: No seller context available');
        throw new Error('Please select a Company/Seller before connecting to Walmart');
    }

    const nonce = generateNonce();
    const state = generateState(sellerContext, 'walmart_initiated');

    // Store state and Walmart callback info in sessionStorage
    sessionStorage.setItem('walmart_oauth_state', state);
    sessionStorage.setItem('walmart_callback_uri', walmartCallbackUri);
    sessionStorage.setItem('walmart_client_type', clientType);
    sessionStorage.setItem('walmart_oauth_company_seller_id', sellerContext.companySellerId);
    // Flag for WalmartSuccess to detect Flow 2 even if URL param is missing
    sessionStorage.setItem('walmart_oauth_flow', 'walmart_initiated');

    // walmartCallbackUri is the Walmart authorization server URL — use it as the base URL.
    // redirectUri must be OUR callback endpoint (same as Flow 1) so Walmart sends the auth code back to us.
    const baseUrl = walmartCallbackUri || WALMART_AUTH_URL;
    const separator = baseUrl.includes('?') ? '&' : '?';
    const authUrl = `${baseUrl}${separator}responseType=code&clientId=${WALMART_CLIENT_ID}&redirectUri=${WALMART_REDIRECT_URI}&nonce=${nonce}&state=${state}&clientType=${clientType}`;

    console.log('Redirecting to Walmart OAuth (Walmart-initiated) for companySellerId:', sellerContext.companySellerId);
    window.location.href = authUrl;
};

/**
 * Disconnect Walmart account for a seller
 * @param {string|number} sellerId - The seller ID to disconnect
 * @returns {Promise<{success: boolean, message?: string}>}
 */
/**
 * Get feed list from Walmart API (via walmart-service)
 * Calls GET /v3/feeds through the adapter
 * @param {string} companySellerId - Required
 * @param {number} [limit=50]
 * @param {number} [offset=0]
 */
export const getWalmartFeedList = async (companySellerId, limit = 50, offset = 0) => {
    try {
        const params = { companySellerId, limit, offset };
        const response = await axios.get(`${WALMART_ADAPTER_URL}/ui/feeds`, { params });
        return response?.data?.data || null;
    } catch (error) {
        const walmartError = error?.response?.data?.error;
        if (walmartError?.code) {
            return { error: walmartError.code, message: walmartError.message };
        }
        console.error('Error fetching Walmart feed list:', error);
        return null;
    }
};

/**
 * Check feed item status from Walmart API (via walmart-service)
 * Calls GET /v3/feeds/{feedId} with includeDetails=true
 * @param {string} feedId
 * @param {string} companySellerId
 */
export const checkWalmartFeedStatus = async (feedId, companySellerId) => {
    try {
        const response = await axios.get(`${WALMART_ADAPTER_URL}/ui/feeds/${feedId}/status`, {
            params: { companySellerId, includeDetails: 'true' }
        });
        return response?.data?.data || null;
    } catch (error) {
        const walmartError = error?.response?.data?.error;
        if (walmartError?.code) {
            return { error: walmartError.code, message: walmartError.message };
        }
        console.error('Error checking feed status:', error);
        return null;
    }
};

/**
 * Submit/resubmit item feed via walmart-service (UI-facing)
 * POST /ui/feeds
 * @param {string} companySellerId
 * @param {object} feedData - Full MP_ITEM payload
 * @param {string} [feedType='item']
 */
export const submitWalmartFeed = async (companySellerId, feedData, feedType = 'item') => {
    try {
        const response = await axios.post(`${WALMART_ADAPTER_URL}/ui/feeds`, {
            companySellerId,
            feedType,
            feedData,
        });
        return response?.data?.data || null;
    } catch (error) {
        const walmartError = error?.response?.data?.error;
        if (walmartError?.code) {
            return { error: walmartError.code, message: walmartError.message };
        }
        console.error('Error submitting Walmart feed:', error);
        return null;
    }
};

/**
 * Get item spec for product types from Walmart
 * @param {number} companySellerId
 * @param {string[]} productTypes - e.g. ["T-Shirts"]
 * @param {string} [feedType] - e.g. "MP_MAINTENANCE"
 */
/**
 * Get product type taxonomy from Walmart
 * @param {number} companySellerId
 * @param {string} [feedType] - e.g. "MP_ITEM"
 */
export const getWalmartTaxonomy = async (companySellerId, feedType = 'MP_ITEM') => {
    try {
        const response = await axios.post(`${WALMART_ADAPTER_URL}/ui/items/taxonomy`, {
            companySellerId,
            filters: { feedType, version: '5.0' },
        });
        return response?.data?.data || null;
    } catch (error) {
        const walmartError = error?.response?.data?.error;
        if (walmartError?.code) {
            return { error: walmartError.code, message: walmartError.message };
        }
        console.error('Error fetching Walmart taxonomy:', error);
        return null;
    }
};

export const getWalmartItemSpec = async (companySellerId, productTypes, fieldNames, feedType = 'MP_MAINTENANCE') => {
    try {
        const response = await axios.post(`${WALMART_ADAPTER_URL}/ui/items/spec`, {
            companySellerId,
            feedType,
            productTypes,
            fieldNames,
        });
        return response?.data?.data || null;
    } catch (error) {
        const walmartError = error?.response?.data?.error;
        if (walmartError?.code) {
            return { error: walmartError.code, message: walmartError.message };
        }
        console.error('Error fetching item spec:', error);
        return null;
    }
};

/**
 * Fetch a single Walmart item by SKU (via walmart-service OAuth proxy)
 * Calls POST /oauth/items with sku filter
 * @param {string|number} companySellerId
 * @param {string} sku
 */
export const getWalmartItemBySku = async (companySellerId, sku) => {
    try {
        const response = await axios.post(`${WALMART_ADAPTER_URL}/ui/items`, {
            companySellerId,
            filters: { sku },
        });
        return response?.data?.data || null;
    } catch (error) {
        const walmartError = error?.response?.data?.error;
        if (walmartError?.code) {
            return { error: walmartError.code, message: walmartError.message };
        }
        console.error('Error fetching Walmart item by SKU:', error);
        return null;
    }
};

export const disconnectWalmartAccount = async (sellerId) => {
    try {
        const response = await axios.delete(`${WALMART_ADAPTER_URL}/auth/walmart/disconnect/${sellerId}`);
        return {
            success: true,
            message: response?.data?.message || 'Disconnected successfully',
        };
    } catch (error) {
        console.error('Error disconnecting Walmart account:', error);
        return { 
            success: false, 
            message: error?.response?.data?.message || 'Failed to disconnect Walmart account' 
        };
    }
};